import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { MapPin, ArrowLeft, Sparkles } from "lucide-react";
import { api, errMsg, media, timeAgo } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Avatar, Skel, ErrorState } from "@/components/common";
import { usePostActions, Actions, PostMenu } from "@/components/PostCard";
import CommentsPanel from "@/components/CommentsPanel";
import ProductCard from "@/components/ProductCard";

export default function PostDetail() {
  const { id } = useParams();
  const [post, setPost] = useState(null);
  const [err, setErr] = useState("");
  const [idx, setIdx] = useState(0);
  const { user, addToCart } = useApp();
  const nav = useNavigate();
  useEffect(() => { setPost(null); api.get(`/posts/${id}`).then((r) => setPost(r.data)).catch((e) => setErr(errMsg(e))); }, [id, user?.id]);
  const a = usePostActions(post || {}, setPost);
  if (err) return <div className="container-x py-10"><ErrorState msg={err} /></div>;
  if (!post) return <div className="container-x grid gap-8 py-10 lg:grid-cols-2"><Skel className="aspect-square" /><Skel className="h-96" /></div>;
  const u = post.user || {};
  const imgs = post.images?.length ? post.images : [];
  return (
    <div className="container-x py-6 md:py-10">
      <button onClick={() => nav(-1)} className="mb-4 flex items-center gap-1 text-sm text-[#78716C] hover:text-[#6B1D2F]" data-testid="post-back-btn"><ArrowLeft className="h-4 w-4" />Back</button>
      <div className="grid gap-8 lg:grid-cols-[minmax(0,540px)_minmax(0,1fr)] lg:gap-12">
        <div className="md:mx-auto md:w-full md:max-w-[560px] lg:mx-0">
          <div className="overflow-hidden rounded-3xl bg-[#F3EDE2]">
            {post.video && idx === imgs.length ? <video src={media(post.video)} controls className="aspect-[4/5] w-full object-cover" />
              : <img src={media(imgs[idx])} alt={post.title} className="aspect-[4/5] w-full object-cover" data-testid="post-main-image" />}
          </div>
          {(imgs.length > 1 || post.video) && (
            <div className="mt-3 flex gap-2">
              {imgs.map((im, i) => <button key={im} onClick={() => setIdx(i)} className={`h-16 w-16 overflow-hidden rounded-xl ring-2 ${idx === i ? "ring-[#D97706]" : "ring-transparent"}`}><img src={media(im)} alt="" className="h-full w-full object-cover" /></button>)}
              {post.video && <button onClick={() => setIdx(imgs.length)} className="h-16 w-16 rounded-xl bg-black text-xs text-white">Video</button>}
            </div>
          )}
        </div>
        <div className="space-y-6">
          <div className="flex items-center gap-3">
            <Link to={`/profile/${post.user_id}`}><Avatar src={u.avatar} name={u.name} size="h-12 w-12" /></Link>
            <div className="flex-1"><Link to={`/profile/${post.user_id}`} className="font-semibold">{u.name}</Link>
              <p className="flex items-center gap-1 text-xs text-[#78716C]"><MapPin className="h-3 w-3" />{post.city} · {timeAgo(post.created_at)}</p></div>
            {user?.id !== post.user_id && <button onClick={a.follow} className={post.following ? "btn-ghost !py-1.5" : "btn-maroon !py-1.5"} data-testid="post-detail-follow-btn">{post.following ? "Following" : "Follow"}</button>}
            <PostMenu post={post} onChange={setPost} onDeleted={() => nav("/community")} />
          </div>
          <div>
            {post.festival && <Link to={`/festivals/${post.festival}`} className="chip mb-3 capitalize">🪔 {post.festival.replace(/-/g, " ")}</Link>}
            <h1 className="font-display text-3xl font-bold text-[#6B1D2F]">{post.title}</h1>
            <p className="mt-2 whitespace-pre-line text-[#57534E]">{post.caption}</p>
            <p className="mt-2 text-sm text-[#D97706]">{post.hashtags?.join(" ")}</p>
          </div>
          <Actions post={post} a={a} big />
          {(post.how_decorated || post.tip) && (
            <div className="space-y-3 rounded-2xl bg-[#FBF7EE] p-4 text-sm">
              {post.how_decorated && <p><b className="text-[#6B1D2F]">How I decorated:</b> {post.how_decorated}</p>}
              {post.products_used && <p><b className="text-[#6B1D2F]">Products used:</b> {post.products_used}</p>}
              {post.tip && <p className="flex gap-2"><Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-[#D97706]" /><span><b className="text-[#6B1D2F]">Tip:</b> {post.tip}</span></p>}
            </div>
          )}
          {post.products?.length > 0 && (
            <div className="rounded-2xl border border-[#E5C378] bg-[#FDF8E2] p-4" data-testid="shop-this-look">
              <p className="font-display text-lg font-semibold text-[#6B1D2F]">Shop this look</p>
              <div className="mt-3 flex flex-wrap gap-2">{post.products.map((p) => <Link key={p.id} to={`/product/${p.slug}`} className="chip !bg-white" data-testid={`tagged-product-${p.slug}`}><img src={p.image} alt="" className="h-5 w-5 rounded-full object-cover" />{p.name}</Link>)}</div>
              <button onClick={() => post.products.forEach((p) => p.stock > 0 && addToCart(p))} className="btn-maroon mt-4 w-full" data-testid="add-look-to-cart">Add entire look to cart</button>
            </div>
          )}
          <div className="card p-5"><p className="font-display mb-4 text-lg font-semibold">Comments</p><CommentsPanel postId={post.id} onCount={(d) => setPost((p) => ({ ...p, comments_count: p.comments_count + d }))} /></div>
        </div>
      </div>
      {post.products?.length > 0 && (
        <section className="mt-14" data-testid="recreate-setup">
          <p className="eyebrow">From this post</p><h2 className="h2 mb-6">Recreate This Puja Setup</h2>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">{post.products.map((p) => <ProductCard key={p.id} p={p} />)}</div>
        </section>
      )}
    </div>
  );
}
